import * as React from 'react'
import useInView from 'hooks/useInView'

/**
 * useArrowNavigation is a hook for arrow navigation in a slider.
 * @param distanceModif
 * distanceModif: number or function that returns a number based on the rect (provides the size of an element and its position relative)
 * @returns lastItemRef, firstItemRef, productListRef, lastItemReached, firstItemReached, handleSlide
 * lastItemRef: reference to the last item in the slider
 * firstItemRef: reference to the first item in the slider
 * productListRef: reference to the slider component
 * lastItemReached: boolean that indicates if the last item is reached
 * firstItemReached: boolean that indicates if the first item is reached
 * handleSlide: function that handles the slide of the slider
 */

export default function useArrowNavigation(
  distanceModif: number | ((rect: DOMRect) => number)
) {
  const [lastItemRef, lastItemReached] = useInView<HTMLDivElement>(50)
  const [firstItemRef, firstItemReached] = useInView<HTMLDivElement>(50)
  const productListRef = React.useRef<null | HTMLDivElement>(null)

  const handleSlide = (direction: 'left' | 'right') => {
    if (!productListRef.current) return

    const rect = productListRef.current.getBoundingClientRect()

    const scrollLeft = productListRef.current.scrollLeft || 0
    const distance =
      typeof distanceModif === 'number'
        ? rect.width * distanceModif
        : distanceModif(rect)
    const scrollX = direction === 'left' ? -distance : distance

    productListRef.current.scroll({
      left: scrollLeft + scrollX,
      behavior: 'smooth'
    })
  }

  return {
    lastItemRef,
    firstItemRef,
    productListRef,
    lastItemReached,
    firstItemReached,
    handleSlide
  }
}
